import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import subscriptionApi, { EffectivePermissions } from '../apis/subscription';

let cachedPermissions: EffectivePermissions | null = null;
let cachedUserId: string | null = null;
let cachedOrgId: string | null = null;
let lastFetchedAt: number = 0;
let inFlightPromise: Promise<EffectivePermissions> | null = null;
let inFlightKey: string | null = null;
const CACHE_TTL_MS = 60 * 1000; // 1 minute cache

export const clearPermissionCache = () => {
    cachedPermissions = null;
    cachedUserId = null;
    cachedOrgId = null;
    lastFetchedAt = 0;
    inFlightPromise = null;
    inFlightKey = null;
};

export const usePermission = (orgId?: string) => {
    const user = useSelector((state: any) => state.auth?.user);
    const isAdmin = user?.role === 'ADMIN';

    const isInitialCacheValid = Boolean(
        cachedPermissions &&
        user?.id &&
        cachedUserId === user.id &&
        cachedOrgId === (orgId || null)
    );

    const [permissions, setPermissions] = useState<EffectivePermissions | null>(
        isInitialCacheValid ? cachedPermissions : null
    );
    const [loading, setLoading] = useState<boolean>(!isInitialCacheValid && !isAdmin);
    const [error, setError] = useState<string | null>(null);

    const fetchPermissions = useCallback(async (force = false) => {
        if (!user?.id) {
            setPermissions(null);
            setLoading(false);
            return;
        }

        const now = Date.now();
        const isCacheValid = Boolean(
            cachedPermissions &&
            cachedUserId === user.id &&
            cachedOrgId === (orgId || null) &&
            now - lastFetchedAt < CACHE_TTL_MS
        );

        if (!force && isCacheValid) {
            setPermissions(cachedPermissions);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const reqKey = `${user.id}:${orgId || ''}`;
            if (!force && inFlightPromise && inFlightKey === reqKey) {
                const data = await inFlightPromise;
                setPermissions(data);
                setError(null);
                return;
            }

            inFlightKey = reqKey;
            inFlightPromise = subscriptionApi.getEffectivePermissions(orgId).then((res) => {
                const data = (res as any)?.data || res;
                cachedPermissions = data;
                cachedUserId = user.id;
                cachedOrgId = orgId || null;
                lastFetchedAt = Date.now();
                return data;
            }).finally(() => {
                if (inFlightKey === reqKey) {
                    inFlightPromise = null;
                    inFlightKey = null;
                }
            });

            const data = await inFlightPromise;
            setPermissions(data);
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch effective permissions:', err);
            setError(err?.message || 'Error fetching permissions');
        } finally {
            setLoading(false);
        }
    }, [orgId, user?.id]);

    useEffect(() => {
        if (user?.id) {
            fetchPermissions();
        } else {
            setPermissions(null);
        }
    }, [fetchPermissions, user?.id]);

    const hasFeature = useCallback((featureKey: string): boolean => {
        if (isAdmin) return true;
        if (!permissions || !permissions.features) return false;
        if (permissions.status !== 'ACTIVE') return false;
        return permissions.features.includes(featureKey);
    }, [permissions, isAdmin]);

    const getQuota = useCallback((quotaKey: string, defaultValue = 0): number => {
        if (isAdmin) return -1;
        if (!permissions || !permissions.quotas) return defaultValue;
        if (permissions.status !== 'ACTIVE') return 0;
        return permissions.quotas[quotaKey] ?? defaultValue;
    }, [permissions, isAdmin]);

    return {
        permissions,
        loading: isAdmin ? false : loading,
        error,
        hasFeature,
        getQuota,
        refresh: () => fetchPermissions(true),
        planCode: permissions?.plan_code || (isAdmin ? 'ADMIN_ALL_ACCESS' : null),
        planName: permissions?.plan_name || (isAdmin ? 'Toàn quyền Quản Trị (Admin)' : 'Gói Miễn Phí'),
        isActive: isAdmin || permissions?.status === 'ACTIVE',
        status: isAdmin ? 'ACTIVE' : (permissions?.status || 'INACTIVE'),
        isVip: isAdmin || Boolean(permissions?.is_vip),
        monthlyCredits: isAdmin ? 999999 : (permissions?.monthly_credits || 0),
        endDate: permissions?.end_date || user?.expires_at || null
    };
};

export default usePermission;
